import {
  apolloClientPrivate,
  apolloClientPublic
} from '../../graphql/apolloClient'
import * as Sentry from '@sentry/browser'
import { createSentrySessionOverview } from '../../app/util/sentry/sentryUtils'
import {
  RefreshSessionDocument,
  RefreshSessionMutationVariables,
  RefreshSessionMutation
} from '../../utils/refreshSession.codegen'
import { differenceInSeconds } from 'date-fns'
import { SessionQuery } from 'front-end/src/public-api/hooks/usePublicApiSession.codegen'
import { staticLinks } from 'shared/linkBuilder'
import { navigateTo } from '../../app/util/appHistory'
import { getRootLooopAppDOMElement } from 'front-end/src/components/utils/rootElResolver'
import * as PublicSchemaTypes from 'front-end/src/graphql/public/PublicSchemaTypes'
import { OrganisationQuery } from 'front-end/src/public-api/hooks/useOrganisation.codegen'

let sessionRefresherInterval: NodeJS.Timeout

const REFRESH_SESSION_CHECK_INTERVAL = 30000 // we have clients with session timeout as low as 300 seconds, so we need to check more often than every 42,857142857 seconds
const sessionRefresher = (
  session: Pick<
    PublicSchemaTypes.UserSessionModelSelf,
    'expires' | 'created_at'
  >
) => {
  if (sessionRefresherInterval) {
    clearInterval(sessionRefresherInterval)
  }
  sessionRefresherInterval = setInterval(async () => {
    const now = new Date()

    const sessionLengthSeconds = differenceInSeconds(
      new Date(session.expires),
      new Date(session.created_at)
    )

    const fromNowToExpirySeconds = differenceInSeconds(
      new Date(session.expires),
      now
    )

    if (sessionLengthSeconds / 7 > fromNowToExpirySeconds) {
      try {
        const refreshResult = await apolloClientPrivate.mutate<
          RefreshSessionMutation,
          RefreshSessionMutationVariables
        >({
          mutation: RefreshSessionDocument
        })
        if (refreshResult.data?.me.refreshSession) {
          sessionRefresher(refreshResult.data.me.refreshSession)
        } else {
          clearInterval(sessionRefresherInterval)
        }
      } catch (err) {
        clearInterval(sessionRefresherInterval)
        throw err
      }
    }
  }, REFRESH_SESSION_CHECK_INTERVAL)
}

export const transitionAppStateToLoggedIn = (
  session: SessionQuery['session'],
  organisation: OrganisationQuery['organisation']
) => {
  if (session?.expires) {
    sessionRefresher({
      expires: session?.expires,
      created_at: session?.created_at
    })
  }
  Sentry.configureScope(function (scope) {
    scope.setUser(createSentrySessionOverview(session, organisation))
  })
}

/** all the dirty stuff we need to do to clean up caches and reset things outside react to the default values as if the user freshly navigated to this page  */
export const transitionAppStateToLoggedOut = async () => {
  clearInterval(sessionRefresherInterval)
  await apolloClientPublic.clearStore()
  await apolloClientPrivate.clearStore()

  getRootLooopAppDOMElement().classList.remove('high-contrast-mode')
  navigateTo(staticLinks.login)
}
